import { useCallback, useEffect, useState } from "react";

export interface MailboxItem {
  subject: string;
  normalizedSubject: string;
  from: string;
  sender: string;
  to: string[];
  cc: string[];
  body: string;
  dateReceived: string;
  dateModified: string;
  importance: string;
  attachments: Array<{
    name: string;
    size: number;
    contentType: string;
    id: string;
    // The raw, un-converted Office attachment id. `id` above is REST-normalised
    // for Graph; `getAttachmentContentAsync` needs the original id (AIVA-6989).
    rawId?: string;
    isInline: boolean;
  }>;
  itemId: string | null;
  conversationId: string | null;
  internetMessageId: string | null;
  itemType: string;
  /** Which Outlook surface is open — `"read"` when a message is open in the
   *  reading pane, `"compose"` when a compose window is (New Email / Reply /
   *  Forward), `null` when no item is open at all.
   *
   *  Detected by probing whether the item's body is *settable*: a message you
   *  are reading exposes `body.getAsync` but not `body.setSelectedDataAsync`;
   *  a compose window exposes both. This is what the Outlook specification
   *  §4.3 describes — compose mode is "detected when the item exposes a
   *  settable body" — and it is the same probe `insertAtCursor` already makes
   *  before writing.
   *
   *  Note this is NOT part of {@link EmailContext}: `buildEmailContext` returns
   *  null when the subject is empty, so a brand-new compose window would lose
   *  the signal entirely and look identical to "no email open". */
  surface: "read" | "compose" | null;
  /**
   * True when the pane is open over a compose window (reply, forward or new
   * mail) rather than a read item. Compose items have no sender to show, so
   * the context banner renders a different variant.
   *
   * Optional so the MailboxItem literals across the test suite keep
   * compiling; useMailboxItem always sets it.
   */
  isCompose?: boolean;
  loading: boolean;
}

const EMPTY_ITEM: MailboxItem = {
  subject: "",
  normalizedSubject: "",
  from: "",
  sender: "",
  to: [],
  cc: [],
  body: "",
  dateReceived: "",
  dateModified: "",
  importance: "",
  attachments: [],
  itemId: null,
  conversationId: null,
  internetMessageId: null,
  itemType: "",
  surface: null,
  isCompose: false,
  loading: false,
};

/** Which Outlook surface an open item represents — see `MailboxItem.surface`. */
function detectSurface(item: unknown): "read" | "compose" | null {
  if (!item) return null;
  const body = (item as { body?: { setSelectedDataAsync?: unknown } }).body;
  return typeof body?.setSelectedDataAsync === "function" ? "compose" : "read";
}

/** Spec §4.3 / §8 ("bounded prompts"): open-email bodies cap at 8,000 characters. */
const MAX_BODY_LENGTH = 8000;

/** AC-2: past the cap the body is truncated "with a `... [truncated]` marker", so
 *  the model — and the user reading the injected context — can see content was
 *  dropped rather than silently reading a sentence that stops mid-word. */
const TRUNCATION_MARKER = "... [truncated]";

/**
 * Read-mode bodies keyed by item id — spec §4.3: the body is "cached per item so
 * switching back is instant". Compose drafts are deliberately never cached: the
 * draft changes as the user types, so a cached copy would go stale immediately.
 *
 * Bounded, because the pane can be pinned and left open across a whole mailbox
 * session; the oldest entry is evicted past the limit.
 */
const bodyCache = new Map<string, string>();
const MAX_CACHED_BODIES = 20;

function cacheBody(key: string, body: string): void {
  // Re-insert so the most recently read item is the newest key (Map preserves
  // insertion order, which is what makes the eviction below LRU rather than FIFO).
  bodyCache.delete(key);
  bodyCache.set(key, body);
  if (bodyCache.size > MAX_CACHED_BODIES) {
    const oldest = bodyCache.keys().next().value;
    if (oldest !== undefined) bodyCache.delete(oldest);
  }
}

/** Drop every cached body. Exported for tests, which reuse item ids across cases. */
export function clearMailBodyCache(): void {
  bodyCache.clear();
}

/**
 * Convert an Outlook itemId / attachment.id to Microsoft Graph REST format.
 *
 * Classic Outlook Desktop returns EWS-format IDs from ``mailboxItem.itemId``
 * (and from each attachment's ``id``) which contain ``+``, ``/``, and ``=``.
 * Graph API endpoints under ``/me/messages/{id}`` expect base64url-encoded
 * REST IDs (using ``-`` and ``_``, no padding). Passing an EWS ID to Graph
 * returns 404 ErrorItemNotFound on every fetch.
 *
 * Office.js ``convertToRestId`` is idempotent — calling it on an already-REST
 * ID returns it unchanged. Wrapped in try/catch because the function is
 * absent on older hosts (Req Set < 1.3); the raw ID is used as a fallback.
 *
 * Exported so the reply-pill target check in the Outlook handlers compares the
 * open item's id in the same form `MailboxItem.itemId` was stored in.
 */
export function toRestId(rawId: string | null | undefined): string | null {
  if (!rawId) return null;
  try {
    const mailbox = Office.context?.mailbox;
    if (typeof mailbox?.convertToRestId === "function") {
      return mailbox.convertToRestId(rawId, Office.MailboxEnums.RestVersion.v2_0);
    }
  } catch {
    // convertToRestId can throw on some hosts when the input is already REST —
    // swallow and return the raw ID.
  }
  return rawId;
}

/**
 * Read a single attachment's bytes from the open Outlook item via Office.js
 * `getAttachmentContentAsync`, returning base64 or `null` when unavailable
 * (AIVA-6989). Takes the RAW (un-REST-converted) attachment id. Only the
 * `base64` content format yields uploadable bytes — cloud (`url`), `.eml`, and
 * iCalendar formats resolve to `null` since they can't be POSTed as file bytes.
 * The manifests already grant `ReadWriteMailbox`, so no Graph scope is needed.
 */
export function readAttachmentBase64(rawId: string): Promise<string | null> {
  return new Promise((resolve) => {
    const item = Office.context?.mailbox?.item as
      | {
          getAttachmentContentAsync?: (
            id: string,
            cb: (r: Office.AsyncResult<Office.AttachmentContent>) => void,
          ) => void;
        }
      | undefined;
    if (!rawId || !item || typeof item.getAttachmentContentAsync !== "function") {
      resolve(null);
      return;
    }
    item.getAttachmentContentAsync(rawId, (result) => {
      if (
        result.status === Office.AsyncResultStatus.Succeeded &&
        result.value &&
        String(result.value.format) === "base64" &&
        result.value.content
      ) {
        resolve(result.value.content);
      } else {
        resolve(null);
      }
    });
  });
}

/**
 * Read the email body via Office.js Body.getAsync (works in both read and compose).
 */
function readBody(body: Office.Body | undefined): Promise<string> {
  return new Promise((resolve) => {
    if (!body || typeof body.getAsync !== "function") {
      resolve("");
      return;
    }
    body.getAsync(Office.CoercionType.Text, (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        const text = result.value ?? "";
        resolve(
          text.length > MAX_BODY_LENGTH
            ? text.substring(0, MAX_BODY_LENGTH) + TRUNCATION_MARKER
            : text,
        );
      } else {
        resolve("");
      }
    });
  });
}

/**
 * Extract email address(es) from a property that may be:
 * - A string (read mode subject)
 * - An EmailAddressDetails object (read mode from)
 * - An array of EmailAddressDetails (read mode to/cc)
 * - An async object with getAsync (compose mode)
 */
function resolveAddresses(
  prop: unknown
): Promise<string[]> {
  return new Promise((resolve) => {
    if (!prop) {
      resolve([]);
      return;
    }

    // Read mode: already an array of EmailAddressDetails
    if (Array.isArray(prop)) {
      resolve(
        prop.map((r: Office.EmailAddressDetails) => r.emailAddress ?? r.displayName ?? "")
      );
      return;
    }

    // Compose mode: has getAsync
    if (typeof prop === "object" && prop !== null && "getAsync" in prop) {
      (prop as { getAsync: (cb: (r: Office.AsyncResult<Office.EmailAddressDetails[]>) => void) => void })
        .getAsync((result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded && Array.isArray(result.value)) {
            resolve(result.value.map((r) => r.emailAddress ?? r.displayName ?? ""));
          } else {
            resolve([]);
          }
        });
      return;
    }

    resolve([]);
  });
}

/**
 * TON-1: read the CURRENTLY open item's body straight from Office.js,
 * bypassing whatever `useMailboxItem`'s own state last cached. Typing into
 * an open compose window never fires `ItemChanged`, so a turn that needs
 * what's on screen right now — not what was there when the pane mounted —
 * must call this instead of trusting `MailboxItem.body`.
 */
export function readCurrentBody(): Promise<string> {
  return readBody(Office.context?.mailbox?.item?.body);
}

/**
 * Reads the current Outlook mailbox item (email or appointment).
 * Handles both read mode (sync properties) and compose mode (async properties).
 * Automatically refreshes when the user selects a different item.
 */
export function useMailboxItem(): MailboxItem {
  const [item, setItem] = useState<MailboxItem>(EMPTY_ITEM);

  const loadItem = useCallback(async () => {
    const mailboxItem = Office.context?.mailbox?.item;
    if (!mailboxItem) {
      setItem(EMPTY_ITEM);
      return;
    }

    setItem((prev) => ({ ...prev, loading: true }));

    // --- Subject ---
    let subject = "";
    if (typeof mailboxItem.subject === "string") {
      // Read mode: subject is a plain string
      subject = mailboxItem.subject;
    } else if (mailboxItem.subject && typeof (mailboxItem.subject as unknown as { getAsync: unknown }).getAsync === "function") {
      // Compose mode: subject has getAsync
      subject = await new Promise<string>((resolve) => {
        (mailboxItem.subject as unknown as { getAsync: (cb: (r: Office.AsyncResult<string>) => void) => void })
          .getAsync((result) => {
            resolve(result.status === Office.AsyncResultStatus.Succeeded ? result.value : "");
          });
      });
    }

    // --- From ---
    let fromAddress = "";
    const fromProp = mailboxItem.from;
    if (fromProp && typeof fromProp === "object" && "emailAddress" in fromProp) {
      // Read mode: from is an EmailAddressDetails
      fromAddress = (fromProp as Office.EmailAddressDetails).emailAddress ?? "";
    }

    // --- To / CC / body ---
    // A read item's body is cached by item id, so flicking back to a message
    // already visited this session skips the getAsync round-trip entirely.
    const surface = detectSurface(mailboxItem);
    const bodyCacheKey = surface === "read" ? (mailboxItem.itemId ?? null) : null;
    const cachedBody = bodyCacheKey ? bodyCache.get(bodyCacheKey) : undefined;

    const [toAddresses, ccAddresses, body] = await Promise.all([
      resolveAddresses(mailboxItem.to),
      resolveAddresses(mailboxItem.cc),
      cachedBody !== undefined ? Promise.resolve(cachedBody) : readBody(mailboxItem.body),
    ]);

    // Only a body we actually read gets cached — readBody resolves "" when
    // getAsync fails, and caching that would make a transient failure permanent.
    if (bodyCacheKey && cachedBody === undefined && body) {
      cacheBody(bodyCacheKey, body);
    }

    // --- Sender (actual sender, may differ from 'from' in delegate scenarios) ---
    let senderAddress = "";
    const senderProp = (mailboxItem as unknown as { sender?: Office.EmailAddressDetails }).sender;
    if (senderProp && typeof senderProp === "object" && "emailAddress" in senderProp) {
      senderAddress = senderProp.emailAddress ?? "";
    }

    // --- Attachments (synchronous in read mode) ---
    // Attachment IDs come back from Office.js in the SAME format as itemId
    // (EWS on classic Outlook, REST on OWA/new Outlook). Normalise to REST
    // so Graph's /me/messages/{id}/attachments/{attId} endpoint accepts them.
    const attachments =
      mailboxItem.attachments?.map((att) => ({
        name: att.name,
        size: att.size,
        contentType: att.contentType ?? "",
        id: toRestId(att.id) ?? "",
        rawId: att.id ?? "",
        isInline: att.isInline ?? false,
      })) ?? [];

    // --- Importance ---
    const importance =
      (mailboxItem as unknown as { importance?: string }).importance ?? "";

    // --- Normalized subject (without RE:/FW: prefixes) ---
    const normalizedSubject =
      (mailboxItem as unknown as { normalizedSubject?: string }).normalizedSubject ?? "";

    // --- Internet Message ID ---
    const internetMessageId =
      (mailboxItem as unknown as { internetMessageId?: string }).internetMessageId ?? null;

    // --- Item type ---
    const itemType = String(mailboxItem.itemType ?? "");

    // --- Compose vs read surface ---
    // `body.setAsync` exists only on mail items in compose mode — the same
    // probe useOpenDraft uses to find an open compose window. Read items have
    // a sender to display; compose items do not, so the context banner needs
    // to know which surface it is describing.
    const isCompose = typeof mailboxItem.body?.setAsync === "function";

    setItem({
      subject,
      normalizedSubject,
      from: fromAddress,
      sender: senderAddress,
      to: toAddresses,
      cc: ccAddresses,
      body,
      dateReceived: mailboxItem.dateTimeCreated?.toISOString() ?? "",
      dateModified: mailboxItem.dateTimeModified?.toISOString() ?? "",
      importance,
      attachments,
      itemId: toRestId(mailboxItem.itemId),
      // AIVA-7003: the conversation id needs the SAME conversion as the item id.
      // Classic Outlook Desktop hands back an EWS-format id here too, and
      // ``$filter=conversationId eq '<EWS id>'`` matches nothing in Graph — so
      // the thread fetch returned zero rows while the body and attachment
      // fetches (which use the converted itemId) worked fine. Spec §4.3:
      // "Item AND conversation IDs are converted to Graph REST IDs for all
      // Graph calls." Only half of that was implemented.
      conversationId: toRestId(mailboxItem.conversationId),
      internetMessageId,
      itemType,
      surface,
      isCompose,
      loading: false,
    });
  }, []);

  useEffect(() => {
    loadItem();

    // Re-load when the user switches to a different email
    if (Office.context?.mailbox) {
      Office.context.mailbox.addHandlerAsync(
        Office.EventType.ItemChanged,
        loadItem
      );
    }

    return () => {
      if (Office.context?.mailbox) {
        Office.context.mailbox.removeHandlerAsync(
          Office.EventType.ItemChanged
        );
      }
    };
  }, [loadItem]);

  return item;
}
